import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelector from "./LanguageSelector";
import "../styles/PlatformDemo.css";

// Le quiz "Carte de transmission" est servi par le front Vision (autre domaine).
const QUIZ_URL = "https://vision.artkiv.com/quiz-collectionneur";
const TAB_ORDER = ["ai", "valuation", "conservation", "provenance"];

const AnimatedNumber = ({ value, formatter, duration = 1200 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const numberRef = useRef(null);

  useEffect(() => {
    if (!numberRef.current) return undefined;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const node = numberRef.current;
    let animationFrame;

    const startAnimation = () => {
      if (reduceMotion) {
        setDisplayValue(value);
        return;
      }

      const startedAt = performance.now();
      const animate = (now) => {
        const progress = Math.min((now - startedAt) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplayValue(value * eased);
        if (progress < 1) animationFrame = requestAnimationFrame(animate);
      };
      animationFrame = requestAnimationFrame(animate);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startAnimation();
          observer.disconnect();
        }
      },
      { threshold: 0.45 },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [duration, value]);

  return <span ref={numberRef}>{formatter(displayValue)}</span>;
};

const PlatformDemo = () => {
  const { i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("ai");
  const [autoPlay, setAutoPlay] = useState(true);
  const demoRef = useRef(null);

  const translations = {
    en: {
      platformLabel: "PLATFORM DEMO",
      home: "Back to home",
      ctaTitle: "This is a sample dossier. Yours starts with one artwork.",
      ctaText:
        "The demo above shows a fictional collection. Create your account to build the same dossier for the works you actually own — or take the three-minute test first to see what your collection needs most.",
      ctaQuiz: "Take the Collector Legacy Test",
      ctaAccount: "Create an account",
      ctaNote: "14-day free trial on the Standard plan. No credit card.",
      disclaimer:
        "Demonstration data. Fictional collections, values and clients.",
      introTitle: "Two powerful views, one comprehensive platform",
      introText:
        "Discover how Artkiv serves both art advisors and family offices with tailored interfaces designed for their specific needs. This example showcases the depth and sophistication of our AI-powered art management system.",
      artAdvisorLabel: "Art Advisor",
      artAdvisorDesc:
        "Detailed artwork analysis with AI insights, valuation tracking, conservation monitoring, and complete provenance documentation",
      familyOfficeLabel: "Family Office",
      familyOfficeDesc:
        "Consolidated dashboard with multi-collection oversight, portfolio analytics, performance tracking, and strategic wealth management",
      artAdvisorView: "Art Advisor View",
      artworkImageText: "Monet, Water Lilies (1919)",
      artworkTitle: "Water Lilies",
      client: "Client",
      medium: "Medium",
      mediumValue: "Oil on canvas",
      dimensions: "Dimensions",
      aiAnalysis: "AI Analysis",
      valuation: "Valuation",
      conservation: "Conservation",
      provenance: "Provenance",
      styleAnalysis: "Style Analysis",
      styleValue: "Impressionism",
      aiVerified: "AI Verified",
      period: "Period",
      periodValue: "Late Impressionist, Giverny series",
      conditionScore: "Condition Score",
      conditionValue: "9.2/10 — Excellent",
      authenticity: "Authenticity",
      authenticityValue: "Certified",
      currentEstimate: "Current Estimate",
      lastAppraisal: "Last Appraisal",
      lastAppraisalValue: "March 2024",
      marketTrend: "Market Trend",
      marketTrendValue: "+12% (12 months)",
      insuranceValue: "Insurance Value",
      lastInspection: "Last Inspection",
      lastInspectionValue: "January 2025",
      storageConditions: "Storage Conditions",
      storageValue: "Climate controlled, UV protected",
      nextRecommended: "Next Recommended",
      nextRecommendedValue: "June 2025",
      riskAssessment: "Risk Assessment",
      riskValue: "Low risk — Optimal conditions",
      provenancePeriod1: "Monet Estate, Giverny",
      provenancePeriod2: "Private collection, Paris",
      provenancePeriod3: "Galerie Moderne, Geneva",
      provenancePeriod4: "2015-Present",
      foView: "Family Office Dashboard",
      totalValue: "Total Value",
      totalValueChange: "+8.2% YTD",
      collections: "Collections",
      collectionsValue: "347 artworks",
      alerts: "Alerts",
      alertsValue: "Conservation reviews",
      portfolioPerformance: "Portfolio Performance (12 months)",
      activeCollections: "Active Collections",
      contemporaryArt: "Contemporary Art",
      durandMeta: "127 artworks • Paris residence",
      durandValue: "€8.2M valuation",
      postImpressionism: "Post-Impressionism",
      moreauName: "Moreau Heritage",
      moreauMeta: "89 artworks • Geneva vault",
      moreauValue: "€12.4M valuation",
      modernSculpture: "Modern Sculpture",
      laurentMeta: "64 artworks • London gallery",
      laurentValue: "€3.1M valuation",
      asianArt: "Asian Art",
      chenMeta: "67 artworks • Hong Kong",
      chenValue: "€820K valuation",
    },
    fr: {
      platformLabel: "DÉMO PLATEFORME",
      home: "Retour à l’accueil",
      ctaTitle:
        "Ceci est un dossier d’exemple. Le vôtre commence par une œuvre.",
      ctaText:
        "La démo ci-dessus présente une collection fictive. Créez votre compte pour construire le même dossier sur les œuvres que vous possédez vraiment — ou faites d’abord le test de trois minutes pour voir ce qui manque le plus à votre collection.",
      ctaQuiz: "Faire le test du collectionneur",
      ctaAccount: "Créer un compte",
      ctaNote: "Essai Standard 14 jours offert. Sans carte bancaire.",
      disclaimer:
        "Données de démonstration. Collections, valeurs et clients fictifs.",
      introTitle: "Deux vues puissantes, une plateforme complète",
      introText:
        "Découvrez comment Artkiv accompagne les conseillers en art et les family offices avec des interfaces sur-mesure adaptées à leurs besoins spécifiques. Cet exemple illustre la profondeur et la sophistication de notre système de gestion d'art alimenté par l'IA.",
      artAdvisorLabel: "Conseiller en Art",
      artAdvisorDesc:
        "Analyse détaillée des œuvres avec insights IA, suivi de valorisation, monitoring de conservation, et documentation complète de provenance",
      familyOfficeLabel: "Family Office",
      familyOfficeDesc:
        "Dashboard consolidé avec supervision multi-collections, analyses de portefeuille, suivi de performance, et gestion patrimoniale stratégique",
      artAdvisorView: "Vue Conseiller en Art",
      artworkImageText: "Monet, Nymphéas (1919)",
      artworkTitle: "Nymphéas",
      client: "Client",
      medium: "Technique",
      mediumValue: "Huile sur toile",
      dimensions: "Dimensions",
      aiAnalysis: "Analyse IA",
      valuation: "Valorisation",
      conservation: "Conservation",
      provenance: "Provenance",
      styleAnalysis: "Analyse de Style",
      styleValue: "Impressionnisme",
      aiVerified: "Vérifié IA",
      period: "Période",
      periodValue: "Impressionnisme tardif, série Giverny",
      conditionScore: "État de Conservation",
      conditionValue: "9,2/10 — Excellent",
      authenticity: "Authenticité",
      authenticityValue: "Certifiée",
      currentEstimate: "Estimation Actuelle",
      lastAppraisal: "Dernière Expertise",
      lastAppraisalValue: "Mars 2024",
      marketTrend: "Tendance Marché",
      marketTrendValue: "+12% (12 mois)",
      insuranceValue: "Valeur Assurance",
      lastInspection: "Dernière Inspection",
      lastInspectionValue: "Janvier 2025",
      storageConditions: "Conditions de Stockage",
      storageValue: "Climatisé, protégé UV",
      nextRecommended: "Prochaine Recommandée",
      nextRecommendedValue: "Juin 2025",
      riskAssessment: "Évaluation Risque",
      riskValue: "Risque faible — Conditions optimales",
      provenancePeriod1: "Succession Monet, Giverny",
      provenancePeriod2: "Collection privée, Paris",
      provenancePeriod3: "Galerie Moderne, Genève",
      provenancePeriod4: "2015-Présent",
      foView: "Dashboard Family Office",
      totalValue: "Valeur Totale",
      totalValueChange: "+8,2% YTD",
      collections: "Collections",
      collectionsValue: "347 œuvres",
      alerts: "Alertes",
      alertsValue: "Examens de conservation",
      portfolioPerformance: "Performance du Portefeuille (12 mois)",
      activeCollections: "Collections Actives",
      contemporaryArt: "Art Contemporain",
      durandMeta: "127 œuvres • Résidence Paris",
      durandValue: "Valorisation €8,2M",
      postImpressionism: "Post-Impressionnisme",
      moreauName: "Héritage Moreau",
      moreauMeta: "89 œuvres • Coffre Genève",
      moreauValue: "Valorisation €12,4M",
      modernSculpture: "Sculpture Moderne",
      laurentMeta: "64 œuvres • Galerie Londres",
      laurentValue: "Valorisation €3,1M",
      asianArt: "Art Asiatique",
      chenMeta: "67 œuvres • Hong Kong",
      chenValue: "Valorisation €820K",
    },
  };

  // La démo suit la langue du site (i18next) au lieu d'un état local figé sur l'anglais.
  const currentLang = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "fr"
  ).startsWith("fr")
    ? "fr"
    : "en";
  const t = translations[currentLang];

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setAutoPlay(false);
  };

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (!autoPlay || reduceMotion) return undefined;

    const timer = window.setInterval(() => {
      setActiveTab((current) => {
        const index = TAB_ORDER.indexOf(current);
        return TAB_ORDER[(index + 1) % TAB_ORDER.length];
      });
    }, 2800);

    return () => window.clearInterval(timer);
  }, [autoPlay]);

  useEffect(() => {
    const root = demoRef.current;
    if (!root) return undefined;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const items = root.querySelectorAll("[data-reveal]");
    if (reduceMotion) {
      items.forEach((item) => item.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -6% 0px" },
    );

    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="platform-demo" ref={demoRef}>
      <div className="header">
        <Link to="/" className="logo" aria-label={t.home}>
          ARTKIV
        </Link>
        <div className="header-right">
          <div className="platform-label">{t.platformLabel}</div>
          <LanguageSelector />
        </div>
      </div>

      <div className="intro-section">
        <div className="intro-content">
          <h2 className="intro-title" data-reveal>
            {t.introTitle}
          </h2>
          <p className="intro-text" data-reveal>
            {t.introText}
          </p>
          <div className="intro-features">
            <div
              className="intro-feature"
              data-reveal
              style={{ "--reveal-delay": "80ms" }}
            >
              <span className="feature-label">{t.artAdvisorLabel}</span>
              <span className="feature-desc">{t.artAdvisorDesc}</span>
            </div>
            <div
              className="intro-feature"
              data-reveal
              style={{ "--reveal-delay": "160ms" }}
            >
              <span className="feature-label">{t.familyOfficeLabel}</span>
              <span className="feature-desc">{t.familyOfficeDesc}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="split-container">
        {/* Art Advisor Side */}
        <div className="advisor-side" data-reveal>
          <div className="side-title">{t.artAdvisorView}</div>

          <div className="artwork-detail">
            <div className="artwork-image demo-artwork-visual">
              <span>{t.artworkImageText}</span>
            </div>

            <h1 className="artwork-title">{t.artworkTitle}</h1>
            <div className="artwork-artist">Claude Monet, 1919</div>

            <div className="info-row">
              <span className="info-label">{t.client}</span>
              <span className="info-value">Sophie Durand Collection</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t.medium}</span>
              <span className="info-value">{t.mediumValue}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t.dimensions}</span>
              <span className="info-value">92 × 73 cm</span>
            </div>

            <div className="tabs" role="tablist">
              {[
                { id: "ai", label: t.aiAnalysis },
                { id: "valuation", label: t.valuation },
                { id: "conservation", label: t.conservation },
                { id: "provenance", label: t.provenance },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`tab ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => handleTabSwitch(tab.id)}
                >
                  <span>{tab.label}</span>
                  {autoPlay && activeTab === tab.id && (
                    <span className="tab-progress" aria-hidden="true" />
                  )}
                </button>
              ))}
            </div>

            {/* AI Analysis Tab */}
            {activeTab === "ai" && (
              <div className="tab-content active">
                <div className="info-row">
                  <span className="info-label">{t.styleAnalysis}</span>
                  <span className="info-value">
                    {t.styleValue}
                    <span className="ai-badge">{t.aiVerified}</span>
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t.period}</span>
                  <span className="info-value">{t.periodValue}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t.conditionScore}</span>
                  <span className="info-value">{t.conditionValue}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t.authenticity}</span>
                  <span className="info-value">
                    {t.authenticityValue}
                    <span className="blockchain-badge">Blockchain</span>
                  </span>
                </div>
              </div>
            )}

            {/* Valuation Tab */}
            {activeTab === "valuation" && (
              <div className="tab-content active">
                <div className="info-row">
                  <span className="info-label">{t.currentEstimate}</span>
                  <span className="info-value">€2,400,000 - €3,200,000</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t.lastAppraisal}</span>
                  <span className="info-value">{t.lastAppraisalValue}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t.marketTrend}</span>
                  <span className="info-value" style={{ color: "#059669" }}>
                    {t.marketTrendValue}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t.insuranceValue}</span>
                  <span className="info-value">€3,500,000</span>
                </div>
              </div>
            )}

            {/* Conservation Tab */}
            {activeTab === "conservation" && (
              <div className="tab-content active">
                <div className="info-row">
                  <span className="info-label">{t.lastInspection}</span>
                  <span className="info-value">{t.lastInspectionValue}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t.storageConditions}</span>
                  <span className="info-value">{t.storageValue}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t.nextRecommended}</span>
                  <span className="info-value">{t.nextRecommendedValue}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t.riskAssessment}</span>
                  <span className="info-value">{t.riskValue}</span>
                </div>
              </div>
            )}

            {/* Provenance Tab */}
            {activeTab === "provenance" && (
              <div className="tab-content active">
                <div className="info-row">
                  <span className="info-label">1919-1945</span>
                  <span className="info-value">{t.provenancePeriod1}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">1945-1978</span>
                  <span className="info-value">{t.provenancePeriod2}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">1978-2015</span>
                  <span className="info-value">{t.provenancePeriod3}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t.provenancePeriod4}</span>
                  <span className="info-value">Sophie Durand Collection</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Family Office Side */}
        <div
          className="fo-side"
          data-reveal
          style={{ "--reveal-delay": "120ms" }}
        >
          <div className="side-title">{t.foView}</div>

          <div className="dashboard-stats">
            <div className="stat-card" data-reveal>
              <div className="stat-label">{t.totalValue}</div>
              <div className="stat-value">
                <AnimatedNumber
                  value={24.5}
                  formatter={(number) => `€${number.toFixed(1)}M`}
                />
              </div>
              <div className="stat-change">{t.totalValueChange}</div>
            </div>
            <div
              className="stat-card"
              data-reveal
              style={{ "--reveal-delay": "90ms" }}
            >
              <div className="stat-label">{t.collections}</div>
              <div className="stat-value">
                <AnimatedNumber
                  value={4}
                  formatter={(number) => Math.round(number)}
                />
              </div>
              <div className="stat-change">{t.collectionsValue}</div>
            </div>
            <div
              className="stat-card"
              data-reveal
              style={{ "--reveal-delay": "180ms" }}
            >
              <div className="stat-label">{t.alerts}</div>
              <div className="stat-value">
                <AnimatedNumber
                  value={2}
                  formatter={(number) => Math.round(number)}
                />
              </div>
              <div className="stat-change">{t.alertsValue}</div>
            </div>
          </div>

          <div className="chart-container" data-reveal>
            <div className="chart-title">{t.portfolioPerformance}</div>
            <div className="chart-placeholder">
              <div className="chart-line"></div>
            </div>
          </div>

          <div className="chart-title" style={{ marginBottom: "24px" }}>
            {t.activeCollections}
          </div>
          <div className="collections-grid">
            <div className="collection-card" data-reveal>
              <div className="collection-image collection-visual-1">
                <span>{t.contemporaryArt}</span>
              </div>
              <div className="collection-info">
                <div className="collection-name">Durand Collection</div>
                <div className="collection-meta">{t.durandMeta}</div>
                <div className="collection-value">{t.durandValue}</div>
              </div>
            </div>

            <div
              className="collection-card"
              data-reveal
              style={{ "--reveal-delay": "90ms" }}
            >
              <div className="collection-image collection-visual-2">
                <span>{t.postImpressionism}</span>
              </div>
              <div className="collection-info">
                <div className="collection-name">{t.moreauName}</div>
                <div className="collection-meta">{t.moreauMeta}</div>
                <div className="collection-value">{t.moreauValue}</div>
              </div>
            </div>

            <div className="collection-card" data-reveal>
              <div className="collection-image collection-visual-3">
                <span>{t.modernSculpture}</span>
              </div>
              <div className="collection-info">
                <div className="collection-name">Laurent Foundation</div>
                <div className="collection-meta">{t.laurentMeta}</div>
                <div className="collection-value">{t.laurentValue}</div>
              </div>
            </div>

            <div
              className="collection-card"
              data-reveal
              style={{ "--reveal-delay": "90ms" }}
            >
              <div className="collection-image collection-visual-4">
                <span>{t.asianArt}</span>
              </div>
              <div className="collection-info">
                <div className="collection-name">Chen Private Trust</div>
                <div className="collection-meta">{t.chenMeta}</div>
                <div className="collection-value">{t.chenValue}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="demo-cta" data-reveal>
        <div className="demo-cta-content">
          <p className="demo-disclaimer">{t.disclaimer}</p>
          <h2 className="demo-cta-title">{t.ctaTitle}</h2>
          <p className="demo-cta-text">{t.ctaText}</p>
          <div className="demo-cta-actions">
            <a className="demo-btn demo-btn-primary" href={QUIZ_URL}>
              {t.ctaQuiz}
            </a>
            <Link className="demo-btn demo-btn-secondary" to="/register">
              {t.ctaAccount}
            </Link>
          </div>
          <p className="demo-cta-note">{t.ctaNote}</p>
        </div>
      </div>
    </div>
  );
};

export default PlatformDemo;
